(() => {
  const keys = ['hanfi:wishlist', 'hanfi_wishlist'];
  const primaryKey = keys[0];

  const normalize = (value) => String(value || '').trim();

  const normalizeRecord = (item) => {
    if (item && typeof item === 'object') {
      const handle = normalize(item.handle || item.productHandle);
      const id = normalize(item.id || item.productId);
      const key = handle || id;
      if (!key) return null;
      return {
        id,
        handle,
        key,
        title: normalize(item.title),
        url: normalize(item.url) || (handle ? `/products/${handle}` : ''),
        image: normalize(item.image || item.featured_image),
        price: Number(item.price) || 0,
      };
    }

    const value = normalize(item);
    if (!value) return null;
    const looksLikeId = /^\d+$/.test(value);
    return {
      id: looksLikeId ? value : '',
      handle: looksLikeId ? '' : value,
      key: value,
      title: '',
      url: looksLikeId ? '' : `/products/${value}`,
      image: '',
      price: 0,
    };
  };

  const readKey = (key) => {
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeRecord).filter(Boolean);
    } catch (_) {
      return [];
    }
  };

  const read = () => {
    const merged = new Map();
    keys.forEach((key) => {
      readKey(key).forEach((item) => {
        const mapKey = item.handle || item.id || item.key;
        if (!mapKey) return;
        merged.set(mapKey, { ...(merged.get(mapKey) || {}), ...item, key: mapKey });
      });
    });
    return Array.from(merged.values());
  };

  const write = (items) => {
    const merged = new Map();
    items.map(normalizeRecord).filter(Boolean).forEach((item) => {
      const mapKey = item.handle || item.id || item.key;
      if (mapKey) merged.set(mapKey, { ...item, key: mapKey });
    });
    const clean = Array.from(merged.values());
    keys.forEach((key) => localStorage.setItem(key, JSON.stringify(clean)));
    return clean;
  };

  const countLabel = (count) => (count > 9 ? '9+' : String(count));

  const itemKeyForButton = (button) => (
    normalize(button.dataset.productHandle) ||
    normalize(button.dataset.productId) ||
    normalize(button.getAttribute('data-product-handle')) ||
    normalize(button.getAttribute('data-product-id'))
  );

  const recordForButton = (button) => {
    const handle = normalize(button.dataset.productHandle);
    const id = normalize(button.dataset.productId);
    const key = handle || id;
    if (!key) return null;
    return {
      id,
      handle,
      key,
      title: normalize(button.dataset.productTitle),
      url: normalize(button.dataset.productUrl) || (handle ? `/products/${handle}` : ''),
      image: normalize(button.dataset.productImage),
      price: Number(button.dataset.productPrice) || 0,
    };
  };

  const setButtonState = (button, active) => {
    button.setAttribute('aria-pressed', String(active));
    button.classList.toggle('is-active', active);
    const icon = button.querySelector('[aria-hidden="true"]') || button;
    if (icon && icon.childElementCount === 0) icon.textContent = active ? '♥' : '♡';
    const title = button.dataset.productTitle || 'this product';
    button.setAttribute('aria-label', active ? `Remove ${title} from wishlist` : `Add ${title} to wishlist`);
  };

  const sync = () => {
    const keysInWishlist = new Set(read().map((item) => item.handle || item.id || item.key));
    document.querySelectorAll('[data-hanfi-wishlist], [data-wishlist-toggle]').forEach((button) => {
      const key = itemKeyForButton(button);
      setButtonState(button, key ? keysInWishlist.has(key) : false);
    });
    document.querySelectorAll('[data-hanfi-wishlist-count]').forEach((badge) => {
      const count = keysInWishlist.size;
      badge.textContent = countLabel(count);
      badge.hidden = count <= 0;
    });
  };

  const formatMoney = (amount) => {
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
    } catch (_) {
      return `Rs. ${(amount / 100).toLocaleString()}`;
    }
  };

  const escapeHTML = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);

  const renderEmpty = (grid) => {
    const empty = document.createElement('section');
    empty.className = 'hanfi-wishlist-page__empty hanfi-glass';
    empty.innerHTML = `
      <div>
        <p>Saved for later</p>
        <h2>Your wishlist is waiting for its first favourite.</h2>
        <span>Explore bestsellers, occasion edits, and new drapes, then tap the heart to keep them here.</span>
        <a href="/collections/all">Browse collections</a>
      </div>
    `;
    grid.append(empty);
  };

  const productCard = (product) => {
    const handle = normalize(product.handle);
    const id = normalize(product.id);
    const title = escapeHTML(product.title || 'Saved product');
    const url = escapeHTML(product.url || (handle ? `/products/${handle}` : '/collections/all'));
    const image = escapeHTML(product.featured_image || '');
    const card = document.createElement('article');
    card.className = 'hanfi-wishlist-card hanfi-glass';
    card.innerHTML = `
      <a class="hanfi-wishlist-card__media" href="${url}">
        ${image ? `<img src="${image}" alt="${title}">` : ''}
      </a>
      <div class="hanfi-wishlist-card__body">
        <h2><a href="${url}">${title}</a></h2>
        ${product.price ? `<p>${formatMoney(product.price)}</p>` : ''}
        <div>
          <a href="${url}">View product</a>
          <button type="button" data-hanfi-wishlist data-product-handle="${escapeHTML(handle)}" data-product-id="${escapeHTML(id)}" data-product-title="${title}" aria-pressed="true">Remove</button>
        </div>
      </div>
    `;
    return card;
  };

  const productFromRecord = (record) => ({
    id: record.id,
    handle: record.handle,
    title: record.title || 'Saved product',
    url: record.url || (record.handle ? `/products/${record.handle}` : '/collections/all'),
    featured_image: record.image,
    price: record.price,
  });

  const fetchProduct = async (record) => {
    if (!record.handle) return productFromRecord(record);
    try {
      const response = await fetch(`/products/${encodeURIComponent(record.handle)}.js`);
      if (!response.ok) throw new Error('Product fetch failed');
      return response.json();
    } catch (_) {
      return productFromRecord(record);
    }
  };

  const renderPage = async () => {
    const grid = document.querySelector('[data-hanfi-wishlist-grid]');
    if (!grid) return;
    const records = read();
    grid.textContent = '';
    if (!records.length) {
      renderEmpty(grid);
      return;
    }

    records
      .filter((record) => record.title || record.handle)
      .map(productFromRecord)
      .forEach((product) => grid.append(productCard(product)));

    const products = await Promise.all(records.map(fetchProduct));
    grid.textContent = '';
    products.filter((product) => product.title || product.handle).forEach((product) => grid.append(productCard(product)));
    if (!grid.children.length) renderEmpty(grid);
    sync();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hanfi-wishlist], [data-wishlist-toggle]');
    if (!button) return;
    const record = recordForButton(button);
    if (!record) return;
    event.preventDefault();
    event.stopPropagation();

    const items = read();
    const key = record.handle || record.id || record.key;
    const index = items.findIndex((item) => (item.handle || item.id || item.key) === key);
    if (index >= 0) items.splice(index, 1);
    else items.push(record);
    write(items);
    sync();
    renderPage();
  }, true);

  window.addEventListener('storage', sync);
  document.addEventListener('DOMContentLoaded', () => {
    write(read());
    sync();
    renderPage();
  });
  document.addEventListener('shopify:section:load', () => {
    sync();
    renderPage();
  });
})();
